import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch } from '../hooks/redux'
import { loginSuccess } from '../store/slices/authSlice'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { getApiUrl } from '../utils/api'
import { hasGuestPortalSupabase, sendPortalPasswordResetEmail, signInPortalUser } from '../services/guestPortalSupabase'

interface LoginFormData {
  email: string
  password: string
}

const LoginPage = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isResetLoading, setIsResetLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  const dispatch = useAppDispatch()
  const router = useCompatRouter()

  useEffect(() => {
    if (router.query.auth !== 'confirmed') {
      return
    }

    setError('')
    setMessage('Email confirmed. You can sign in now.')
  }, [router.query.auth])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('')
    setMessage('')
  }

  const handlePasswordReset = async () => {
    const email = formData.email.trim()

    if (!email) {
      setError('Enter your email address first, then use Forgot password.')
      setMessage('')
      return
    }

    if (!hasGuestPortalSupabase()) {
      setError('Password recovery is not configured right now.')
      setMessage('')
      return
    }

    setIsResetLoading(true)
    setError('')
    setMessage('')

    try {
      await sendPortalPasswordResetEmail(email)
      setMessage('Reset link sent. Check your inbox and spam folder, then open the link on this device.')
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to send reset link right now.')
    } finally {
      setIsResetLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setMessage('')

    try {
      if (hasGuestPortalSupabase()) {
        try {
          const sessionState = await signInPortalUser(formData.email.trim(), formData.password)
          dispatch(loginSuccess(sessionState))

          if (sessionState.user.role === 'admin') {
            await router.push('/booking-system/admin')
          } else {
            await router.push('/booking-system/dashboard')
          }
          return
        } catch {
          // Fall back to the legacy admin auth path if this account only exists there.
        }
      }

      const response = await fetch(`${getApiUrl()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      })

      const result = await response.json()

      const profile = result?.data?.profile || result?.data?.user

      if (!response.ok || !result?.success || !result?.data?.token || !profile) {
        setError(result?.error || 'Invalid email or password. Please try again.')
        return
      }

      dispatch(loginSuccess({
        token: result.data.token,
        user: {
          id: String(profile.id || profile.email || formData.email),
          email: profile.email || formData.email,
          firstName: profile.first_name || 'User',
          lastName: profile.last_name || '',
          role: profile.role === 'admin' ? 'admin' : 'guest'
        }
      }))

      if (profile.role === 'admin') {
        await router.push('/booking-system/admin')
      } else {
        await router.push('/booking-system/dashboard')
      }
    } catch {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="admin-login-theme min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl items-center justify-center">
        <div className="admin-login-panel">
          <aside className="admin-login-aside">
            <div>
              <span className="admin-login-kicker">Admin Access</span>
              <h1 className="text-4xl font-bold leading-tight text-white">Manage Marina Glen with a cleaner, faster control desk.</h1>
              <p className="mt-4 max-w-md text-base text-white/80">
                Access bookings, availability, guests, pricing and seasonal settings from one streamlined dashboard.
              </p>
              <ul className="admin-login-feature-list">
                <li>Real-time reservation oversight</li>
                <li>Guest, unit and payment management</li>
                <li>Seasonal pricing and booking controls</li>
              </ul>
            </div>
            <div className="text-sm text-white/70">
              <p>Need the public site?</p>
              <Link to="/booking-system" className="mt-2 inline-flex text-sm font-semibold text-white hover:text-white/80 transition-colors">
                Return to booking home
              </Link>
            </div>
          </aside>

          <section className="admin-login-content">
            <div className="mb-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Secure Sign-In</p>
              <h2 className="mt-3 text-4xl font-bold text-black">Sign In</h2>
              <p className="mt-3 text-base text-slate-600">Access your Marina Glen Holiday Resort account.</p>
            </div>

            {error && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {message && (
              <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-sm text-emerald-700">{message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 pr-24 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={isResetLoading}
                    className="text-sm font-semibold text-sky-700 transition-colors hover:text-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isResetLoading ? 'Sending reset link...' : 'Forgot password?'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-2xl bg-slate-950 px-4 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-8 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Don't have an account?{' '}
                <Link to="/booking-system/register" className="font-semibold text-sky-700 hover:text-sky-800">
                  Register here
                </Link>
              </p>
              <Link
                to="/booking-system"
                className="font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                ← Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default LoginPage